import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Mail, ArrowLeft, KeyRound, CheckCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const ForgotPasswordPage = () => {
  const [step, setStep] = useState(1); // 1: email, 2: code, 3: new password, 4: success
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [devToken, setDevToken] = useState(''); // Development mode token

  const handleRequestCode = async (e) => {
    e.preventDefault();
    
    if (!email) {
      toast.error('E-posta adresi gerekli');
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/auth/forgot-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email })
      });

      const data = await response.json();

      if (response.ok) {
        toast.success('Doğrulama kodu gönderildi');
        setStep(2);
        // Development mode - gerçek üretimde bu kaldırılmalı
        if (data._dev_token) {
          setDevToken(data._dev_token);
        }
      } else {
        toast.error(data.detail || 'Bir hata oluştu');
      }
    } catch (error) {
      toast.error('Sunucu hatası');
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();

    if (!code || code.length !== 6) {
      toast.error('6 haneli kod gerekli');
      return;
    }

    if (!newPassword || newPassword.length < 6) {
      toast.error('Şifre en az 6 karakter olmalı');
      return;
    }

    if (newPassword !== confirmPassword) {
      toast.error('Şifreler eşleşmiyor');
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/auth/reset-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token: code, new_password: newPassword })
      });

      const data = await response.json();

      if (response.ok) {
        toast.success('Şifreniz başarıyla değiştirildi');
        setStep(4);
      } else {
        toast.error(data.detail || 'Bir hata oluştu');
      }
    } catch (error) {
      toast.error('Sunucu hatası');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-gradient-to-br from-emerald-500 to-teal-600 mb-4">
            <KeyRound className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-2xl font-bold text-white">Şifremi Unuttum</h1>
          <p className="text-slate-400 mt-2">
            {step === 1 && 'E-posta adresinizi girin'}
            {step === 2 && 'Doğrulama kodunu girin'}
            {step === 3 && 'Yeni şifrenizi belirleyin'}
            {step === 4 && 'İşlem tamamlandı'}
          </p>
        </div>

        {/* Card */}
        <div className="bg-slate-800/50 backdrop-blur-xl rounded-2xl border border-slate-700/50 p-6">
          {/* Step 1: Email */}
          {step === 1 && (
            <form onSubmit={handleRequestCode} className="space-y-4">
              <div>
                <label className="text-sm font-medium text-slate-300 mb-2 block">
                  E-posta Adresi
                </label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                  <Input
                    type="email"
                    placeholder="ornek@email.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="pl-10 bg-slate-900/50 border-slate-600 text-white"
                    data-testid="forgot-email-input"
                  />
                </div>
              </div>

              <Button
                type="submit"
                disabled={loading}
                className="w-full bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700"
                data-testid="forgot-submit-btn"
              >
                {loading ? 'Gönderiliyor...' : 'Kod Gönder'}
              </Button>
            </form>
          )}

          {/* Step 2: Code & New Password */}
          {step === 2 && (
            <form onSubmit={handleResetPassword} className="space-y-4">
              {/* Development mode token display */}
              {devToken && (
                <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-3 mb-4">
                  <p className="text-amber-400 text-xs font-medium">Geliştirme Modu - Doğrulama Kodu:</p>
                  <p className="text-amber-300 text-2xl font-bold tracking-widest mt-1">{devToken}</p>
                </div>
              )}

              <div>
                <label className="text-sm font-medium text-slate-300 mb-2 block">
                  Doğrulama Kodu (6 haneli)
                </label>
                <Input
                  type="text"
                  placeholder="000000"
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  className="bg-slate-900/50 border-slate-600 text-white text-center text-2xl tracking-widest"
                  maxLength={6}
                  data-testid="reset-code-input"
                />
              </div>

              <div>
                <label className="text-sm font-medium text-slate-300 mb-2 block">
                  Yeni Şifre
                </label>
                <Input
                  type="password"
                  placeholder="••••••••"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  className="bg-slate-900/50 border-slate-600 text-white"
                  data-testid="reset-password-input"
                />
              </div>

              <div>
                <label className="text-sm font-medium text-slate-300 mb-2 block">
                  Şifre Tekrar
                </label>
                <Input
                  type="password"
                  placeholder="••••••••"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="bg-slate-900/50 border-slate-600 text-white"
                  data-testid="reset-confirm-input"
                />
              </div>

              <Button
                type="submit"
                disabled={loading}
                className="w-full bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700"
                data-testid="reset-submit-btn"
              >
                {loading ? 'Değiştiriliyor...' : 'Şifreyi Değiştir'}
              </Button>

              <button
                type="button"
                onClick={() => setStep(1)}
                className="w-full text-sm text-slate-400 hover:text-white transition-colors"
              >
                Farklı e-posta kullan
              </button>
            </form>
          )}

          {/* Step 4: Success */}
          {step === 4 && (
            <div className="text-center py-4">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-emerald-500/20 mb-4">
                <CheckCircle className="w-8 h-8 text-emerald-500" />
              </div>
              <h3 className="text-lg font-semibold text-white mb-2">Şifre Değiştirildi!</h3>
              <p className="text-slate-400 text-sm mb-6">
                Yeni şifrenizle giriş yapabilirsiniz.
              </p>
              <Link to="/login">
                <Button className="w-full bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700">
                  Giriş Yap
                </Button>
              </Link>
            </div>
          )}
        </div>

        {/* Back to login */}
        <div className="mt-6 text-center">
          <Link
            to="/login"
            className="inline-flex items-center gap-2 text-slate-400 hover:text-white transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Giriş sayfasına dön
          </Link>
        </div>
      </div>
    </div>
  );
};

export default ForgotPasswordPage;
